import React, { useState } from 'react';
import Hero from '../../../components/Hero/Hero';
import Pathfinder from '../../../components/Pathfinder/Pathfinder';
import Metadata from '../../../components/Metadata/Metadata';
import Section from '../../../components/Section/Section';
import Card from '../../../components/Card/Card';
import GlobalCallout from '../../../components/Global/CalloutDefault';
import { Box, Typography } from '@mui/material';
import Accordion from '../../../components/Accordion/Accordion';
import Wallpaper from '../../../components/Wallpaper/Wallpaper';
import Button from '../../../components/Button/Button';
import Image from '../../../components/Image/Image';
import ContentSplit from '../../../components/ContentSplit/ContentSplit';
import ListItem from '../../../components/ListItem/ListItem';
import { Link } from 'react-router-dom';

const PageContent: React.FC = () => {
  const [expandAll, setExpandAll] = useState(false);

  const toggleAll = () => {
    setExpandAll(!expandAll);
  };

  return (
    <Box>
      <Metadata
        title="Government Public Health Solutions | General Genomics"
        description="Explore General Genomics’ secure, scalable data solutions for public health. Learn how we use synthetic data (SYN46™), epidemiological modeling, and predictive analytics to tackle public health challenges."
      />
      <Hero
        helper="Industries &gt; Government"
        headline="Scalable data solutions for public health agencies and programs"
      />

      <Section theme="" className="cards-large-icons">
        <h2 className="action-on-view action-fade-in">
          Empowering Public Health with Data Solutions
        </h2>
        <p className="action-on-view action-fade-in">
          Our cutting-edge data solutions are designed to support government
          agencies and public health organizations in addressing critical
          challenges. With expertise in synthetic and de-identified data,
          epidemiological modeling, and predictive analytics, we help drive
          informed decisions that protect public health. Our solutions are
          secure, scalable, and fully compliant with industry standards like
          HIPAA and GDPR, ensuring that sensitive data remains safe and
          trustworthy. Contact us to explore how our tools can enhance your
          public health efforts.
        </p>
        <Image
          path="/images/library/office-soldier-desk.webp"
          size="wide"
          className="action-on-view action-fade-in"
          alt="This is a wide image."
        />
      </Section>

      <ContentSplit
        image="/images/library/military-meeting-woman-laptop.webp"
        position="right"
      >
        <p className="helper action-on-view action-fade-in">
          Explore Our Solutions
        </p>
        <h2 className="action-on-view action-fade-in">
          Tailored Data Solutions for Public Health Agencies
        </h2>
        <ListItem
          icon="check"
          iconClass="highlighted-icon"
          className="custom-list-item"
        >
          <p>
            <strong>Predictive Analytics:</strong> Leverage advanced data models
            to predict health trends and prepare for future public health
            challenges.
          </p>
        </ListItem>
        <ListItem
          icon="check"
          iconClass="highlighted-icon"
          className="custom-list-item"
        >
          <p>
            <strong>De-identified Data:</strong> Use safe and secure synthetic
            data for research, analysis, and decision-making, ensuring privacy
            compliance.
          </p>
        </ListItem>
        <ListItem
          icon="check"
          iconClass="highlighted-icon"
          className="custom-list-item"
        >
          <p>
            <strong>Government Compliance:</strong> Rest easy knowing our
            solutions meet stringent regulations like HIPAA and GDPR to protect
            sensitive data.
          </p>
        </ListItem>
        <ListItem
          icon="check"
          iconClass="highlighted-icon"
          className="custom-list-item"
        >
          <p>
            <strong>Real-time Disease Surveillance:</strong> Monitor and respond
            to health trends with our integrated tools for real-time data
            analysis.
          </p>
        </ListItem>
      </ContentSplit>

      <Section theme="dark" className="section-cross-linking">
        <h2 className="action-on-view action-fade-in">
          Explore More About General Genomics
        </h2>
        <p className="action-on-view action-fade-in">
          Discover key areas of our business and learn how General Genomics
          leads the way in data-driven genomics and health solutions.
        </p>
        <div className="card-group">
          <Card
            icon="security"
            headline="Data Governance & Compliance"
            subhead="Learn about our commitment to data security and compliance with global standards."
            btnURL="/our-approach/data-governance-and-compliance"
            btnLabel="Learn more"
            className="action-on-view action-fade-in"
          />
          <Card
            icon="settings"
            headline="Synthetic Data (SYN46™) Solutions"
            subhead="Explore how our synthetic data (SYN46™) technology is revolutionizing public health applications."
            btnURL="/innovation/syn46"
            btnLabel="Learn more"
            className="action-on-view action-fade-in"
          />
          <Card
            icon="work"
            headline="How It Works"
            subhead="Dive into the capabilities of our advanced genomics data platform and its applications."
            btnURL="/data-portal/how-it-works"
            btnLabel="Learn more"
            className="action-on-view action-fade-in"
          />
        </div>
      </Section>

      <Wallpaper asset="../../images/library/lab-technicians-group-laptop-5.webp"></Wallpaper>

      <Section>
        <Typography variant="h2" className="action-on-view action-fade-in">
          Frequently asked questions about General Genomics&apos; public health
          data solutions
        </Typography>
        <Typography className="action-on-view action-fade-in">
          Learn how General Genomics provides secure, scalable data solutions
          for government agencies to address public health challenges.
        </Typography>
        <Box className="accordion-wrapper action-on-view action-fade-in">
          <Button
            className="accordion-button"
            type="outlined"
            onClick={toggleAll}
            icon={expandAll ? 'expand_less' : 'expand_more'}
            label={expandAll ? 'Collapse All' : 'Expand All'}
          />
          <Accordion
            isOpen={expandAll}
            headline="What public health data solutions does General Genomics provide?"
          >
            General Genomics offers secure, scalable, and compliant data
            solutions tailored for{' '}
            <Link to="/industries/government">government</Link> agencies and
            public health organizations, including synthetic and de-identified
            data, epidemiological modeling, and predictive analytics to tackle
            public health challenges.
          </Accordion>
          <Accordion
            isOpen={expandAll}
            headline="How does General Genomics ensure data compliance with government standards?"
          >
            Our solutions are fully compliant with key regulations like HIPAA
            and GDPR, ensuring that all data handling meets the highest security
            and privacy standards.
          </Accordion>
          <Accordion
            isOpen={expandAll}
            headline="Can General Genomics help with disease surveillance and health policy analytics?"
          >
            Yes, we provide advanced data solutions for disease surveillance,
            health trend analysis, and health policy analytics, helping{' '}
            <Link to="/industries/government">government</Link> clients make
            informed decisions and improve public health outcomes.
          </Accordion>
          <Accordion
            isOpen={expandAll}
            headline="How can I learn more about General Genomics’ data platform and capabilities?"
          >
            You can explore our data platform capabilities by visiting our Data
            Portal page or contacting us to see how our solutions can meet your
            specific needs.
          </Accordion>
          <Accordion
            isOpen={expandAll}
            headline="How do I get in touch with General Genomics for more information?"
          >
            You can contact us directly by clicking the{' '}
            <Link to="/contact-us">&quot;Contact Us&quot;</Link> button on our
            website for personalized assistance.
          </Accordion>
        </Box>
      </Section>

      <Section theme="dark" bgName="lines" className="section-call-to-action">
        <Typography variant="h2" className="action-on-view action-fade-in">
          Why partnering with General Genomics transforms bioinformatics
          research and innovation
        </Typography>
        <Box className="card-group">
          <Card
            icon="check"
            headline="Advanced Solutions in Analytics"
            subhead="General Genomics combines analytics and innovation with cutting-edge algorithms, large language models, machine learning, and predictive analytics to transform synthetic systems and complex data insights."
            className="action-on-view action-fade-in"
          />
          <Card
            icon="check"
            headline="Precision Insights"
            subhead="Harness predictive modeling and genomic data analysis to drive accurate, actionable insights tailored to research and healthcare objectives."
            className="action-on-view action-fade-in action-delay-250"
          />
          <Card
            icon="check"
            headline="Collaborative Expertise"
            subhead="Partner with a team experienced in supporting government initiatives to advance bioinformatics research and public health strategies."
            className="action-on-view action-fade-in action-delay-500"
          />
        </Box>
      </Section>

      <GlobalCallout />

      <Pathfinder />
    </Box>
  );
};

export default PageContent;
